/**
 * Formato para columnas de participantes/inscritos en listas y tablas.
 * - Nombres y apellidos: en MAYÚSCULAS.
 * - RUT: con guión antes del dígito verificador (ej: 12345678-9).
 * - Pesos: 100.0 → "100", 67.5 → "67.5" (sin decimal cuando es .0).
 * - Categoría edad: "SUB JUNIOR 15-19" → "SUB JUNIOR" (sin el rango de edades).
 * - Convención ACHIPO en lista de participantes: Hombres -140 / 140+, Mujeres -90 / 90+.
 */

/**
 * Nombre o apellido para mostrar: en mayúsculas.
 * @param {string|null|undefined} val
 * @returns {string}
 */
export function formatNombre(val) {
  if (val === null || val === undefined || val === '') return '—';
  const s = String(val).trim();
  return s ? s.toUpperCase() : '—';
}

/**
 * RUT chileno con guión antes del dígito verificador (ej: 12345678-9, 12345678-K).
 * @param {string|number|null|undefined} val
 * @returns {string}
 */
export function formatRut(val) {
  if (val === null || val === undefined || val === '') return '—';
  let s = String(val).trim();
  if (!s) return '—';
  s = s.replace(/\./g, '').replace(/\s/g, '');
  const match = s.match(/^(\d{1,8})([\dkK])$/i);
  if (match) return `${match[1]}-${match[2].toUpperCase()}`;
  if (s.length >= 2 && /[\dkK]$/i.test(s) && /^\d+$/.test(s.slice(0, -1))) return `${s.slice(0, -1)}-${s.slice(-1).toUpperCase()}`;
  return s || '—';
}

/**
 * Formatea peso: quita el .0 cuando es entero (100.0 → 100); deja decimal si tiene .5, .7, etc.
 * @param {string|number|null|undefined} val
 * @returns {string} Valor para mostrar o '—' si vacío
 */
export function formatPeso(val) {
  if (val === null || val === undefined || val === '') return '—';
  const s = String(val).trim();
  if (!s) return '—';
  const n = parseFloat(s.replace(',', '.'));
  if (Number.isNaN(n)) return s;
  if (Number.isInteger(n)) return String(Math.round(n));
  return String(Number(n.toFixed(2)));
}

/**
 * Formatea categoría de peso para la lista de participantes: Hombres = -140 y 140+, Mujeres = -90 y 90+.
 * @param {string|number|null|undefined} val - Valor de categoria_peso_tested o categoria_peso_untested
 * @param {string|null|undefined} division - Género/división (Masculino, Femenino, Hombres, Mujeres, etc.)
 * @returns {string}
 */
/** Normaliza texto tipo "más de 140" / "mas de 140" al formato ACHIPO "140+". */
function normalizarMasDePeso(s) {
  const t = s.trim().toLowerCase();
  if (t === 'más de 140' || t === 'mas de 140') return '140+';
  if (t === 'más de 90' || t === 'mas de 90') return '90+';
  return s;
}

export function formatPesoCategoriaLista(val, division) {
  if (val === null || val === undefined || val === '') return '—';
  let s = String(val).trim();
  if (!s) return '—';
  s = normalizarMasDePeso(s);
  const esHombres = /varon|masc|masculino|hombre|male/i.test(String(division || ''));
  const tieneMas = /\+/.test(s);
  const num = parseFloat(s.replace(/\+/g, '').replace(',', '.').trim());
  if (!Number.isNaN(num)) {
    if (num === 140 && tieneMas) return '140+';
    if (num === 140 && !tieneMas) return esHombres ? '-140' : String(Math.round(num));
    if (num === 90 && tieneMas) return '90+';
    if (num === 90 && !tieneMas) return esHombres ? '90' : '-90';
  }
  return formatPeso(val);
}

/**
 * Quita los rangos de edades de la categoría en todo el texto (ej: "JUNIOR 20-23, OPEN" → "JUNIOR, OPEN").
 * @param {string|null|undefined} val
 * @returns {string}
 */
export function formatCategoriaEdad(val) {
  if (val === null || val === undefined || val === '') return '—';
  let s = String(val).trim();
  if (!s) return '—';
  // Quitar rangos de edad en cualquier parte: " 15-19", " 20-23", " 24-39", " 40+"
  s = s.replace(/\s+\d{1,2}(-\d{1,2})?(\s*\+\s*)?/gi, ' ');
  // Unificar saltos de línea y espacios múltiples; limpiar comas
  s = s.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').replace(/\s*,\s*/g, ', ').replace(/\s*,\s*,/g, ',').trim();
  s = s.replace(/^,\s*|\s*,$/g, '').trim();
  return s || '—';
}
